// Client-side structured logger. Buffers events and flushes to /api/logs,
// which forwards to Seq over the internal Docker network. Browser never
// sees the Seq URL or any credential; user/workspace/org identity is
// enriched server-side from the iron-session cookie.

import { ApiError } from "@/lib/api/errors"

export type LogLevel = "debug" | "info" | "warn" | "error"

export type LogEvent = {
  ts: string
  level: LogLevel
  event: string
  props?: Record<string, unknown>
  error?: { message: string; code?: string; status?: number; traceId?: string; stack?: string }
}

const MAX_BUFFER = 100
const MAX_PROP_LEN = 512
const FLUSH_INTERVAL_MS = 5000
const DISALLOWED_PROP_KEYS = new Set([
  "password", "pwd", "passwd",
  "token", "accesstoken", "refreshtoken", "access_token", "refresh_token",
  "secret", "apikey", "api_key",
  "authorization", "cookie",
])

const IS_PROD = process.env.NODE_ENV === "production"

let buffer: LogEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function nowIso(): string {
  return new Date().toISOString()
}

function sanitizeProps(props: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!props) return undefined
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(props)) {
    if (DISALLOWED_PROP_KEYS.has(k.toLowerCase())) continue
    out[k] = truncate(v)
  }
  return out
}

function truncate(v: unknown, depth = 0): unknown {
  if (depth > 3) return "[depth-capped]"
  if (v == null) return v
  if (typeof v === "string") return v.length > MAX_PROP_LEN ? v.slice(0, MAX_PROP_LEN) + "…" : v
  if (typeof v === "number" || typeof v === "boolean") return v
  if (Array.isArray(v)) return v.slice(0, 20).map((x) => truncate(x, depth + 1))
  if (typeof v === "object") {
    const out: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(v as object)) {
      if (DISALLOWED_PROP_KEYS.has(k.toLowerCase())) continue
      out[k] = truncate(val, depth + 1)
    }
    return out
  }
  return String(v).slice(0, MAX_PROP_LEN)
}

function errorFields(err: unknown): LogEvent["error"] | undefined {
  if (err == null) return undefined
  if (err instanceof ApiError) {
    return {
      message: truncate(err.message) as string,
      code: err.code,
      status: err.status,
      traceId: err.problem?.traceId,
      stack: IS_PROD ? undefined : (typeof err.stack === "string" ? err.stack.slice(0, 2000) : undefined),
    }
  }
  if (err instanceof Error) {
    return {
      message: truncate(err.message) as string,
      stack: IS_PROD ? undefined : (typeof err.stack === "string" ? err.stack.slice(0, 2000) : undefined),
    }
  }
  return { message: truncate(String(err)) as string }
}

function push(level: LogLevel, event: string, props?: Record<string, unknown>, error?: unknown): void {
  if (level === "debug" && IS_PROD) return
  if (typeof window === "undefined") return
  const entry: LogEvent = {
    ts: nowIso(),
    level,
    event,
    props: sanitizeProps(props),
    error: errorFields(error),
  }
  buffer.push(entry)
  if (buffer.length >= MAX_BUFFER || level === "error") {
    void flush()
    return
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => { void flush() }, FLUSH_INTERVAL_MS)
  }
}

async function flush(useBeacon = false): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (buffer.length === 0) return
  const batch = buffer
  buffer = []
  const body = JSON.stringify({ events: batch, flushedAt: nowIso() })
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" })
      navigator.sendBeacon("/api/logs", blob)
      return
    }
    await fetch("/api/logs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
  } catch {
    // Swallow — the logger must never break the app. Events are dropped.
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush(true)
  })
  window.addEventListener("pagehide", () => { void flush(true) })
}

export const log = {
  debug: (event: string, props?: Record<string, unknown>) => push("debug", event, props),
  info:  (event: string, props?: Record<string, unknown>) => push("info", event, props),
  warn:  (event: string, props?: Record<string, unknown>) => push("warn", event, props),
  error: (event: string, errorOrProps?: unknown, props?: Record<string, unknown>) => {
    if (errorOrProps instanceof Error || errorOrProps instanceof ApiError) {
      push("error", event, props, errorOrProps)
    } else if (errorOrProps && typeof errorOrProps === "object") {
      push("error", event, errorOrProps as Record<string, unknown>)
    } else {
      push("error", event, props)
    }
  },
}
